import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, Suspense, lazy, useState } from 'react';
import { SEO } from './components/SEO';
import Layout from './components/Layout';
import { Toaster } from 'sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
// Lazy load components
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Reviews = lazy(() => import('./pages/Reviews'));
const ForDiasporaFamilies = lazy(() => import('./pages/ForDiasporaFamilies'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const FarajaConversations = lazy(() => import('./pages/FarajaConversations'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const FarajaBooking = lazy(() => import('./pages/FarajaBooking'));
const BookingConfirmed = lazy(() => import('./pages/BookingConfirmed'));
const MasterServices = lazy(() => import('./pages/MasterServices'));
const LivestreamingService = lazy(() => import('./pages/LivestreamingService'));
const PhotographyService = lazy(() => import('./pages/PhotographyService'));
const VideoService = lazy(() => import('./pages/VideoProduction'));
const DroneService = lazy(() => import('./pages/DroneService'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const NotFound = lazy(() => import('./pages/NotFound'));



function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function App() {
  const [adminData] = useState({
    bookings: [],
    leads: [],
    reviews: [],
    posts: [],
  });

  return (
    <Router>
      <ScrollToTop />
      <Toaster
        position="top-center"
        expand={false}
        richColors
        closeButton
        duration={4000}
      />
      <Routes>
        <Route path="/" element={<Layout><Suspense fallback={<LoadingSpinner />}><Home /></Suspense></Layout>} />
        <Route path="/about" element={<Layout><Suspense fallback={<LoadingSpinner />}><About /></Suspense></Layout>} />
        <Route path="/contact" element={<Layout><Suspense fallback={<LoadingSpinner />}><Contact /></Suspense></Layout>} />
        <Route path="/reviews" element={<Layout><Suspense fallback={<LoadingSpinner />}><Reviews /></Suspense></Layout>} />
        <Route path="/for-diaspora-families" element={<Layout><Suspense fallback={<LoadingSpinner />}><ForDiasporaFamilies /></Suspense></Layout>} />
        <Route path="/diaspora-funeral-services" element={<Navigate to="/for-diaspora-families" replace />} />
        <Route path="/diaspora" element={<Navigate to="/for-diaspora-families" replace />} />
        <Route path="/blog" element={<Layout><Suspense fallback={<LoadingSpinner />}><Blog /></Suspense></Layout>} />
        <Route path="/blog/faraja-conversations" element={<Layout><Suspense fallback={<LoadingSpinner />}><FarajaConversations /></Suspense></Layout>} />
        <Route path="/blog/:slug" element={<Layout><Suspense fallback={<LoadingSpinner />}><BlogPost /></Suspense></Layout>} />
        <Route path="/faraja-conversations" element={<Layout><Suspense fallback={<LoadingSpinner />}><FarajaConversations /></Suspense></Layout>} />
        <Route path="/portfolio" element={<Layout><Suspense fallback={<LoadingSpinner />}><Portfolio /></Suspense></Layout>} />
        <Route path="/book" element={<Navigate to="/booking" replace />} />
        <Route path="/book-now" element={<Navigate to="/booking" replace />} />
        <Route path="/booking" element={<Layout><Suspense fallback={<LoadingSpinner />}><FarajaBooking /></Suspense></Layout>} />
        <Route path="/booking-confirmed" element={<Layout><Suspense fallback={<LoadingSpinner />}><BookingConfirmed /></Suspense></Layout>} />
        <Route path="/services" element={<Layout><Suspense fallback={<LoadingSpinner />}><MasterServices /></Suspense></Layout>} />

        {/* Legacy service redirects (404 fixes for old links) */}
        <Route path="/services/livestreaming" element={<Navigate to="/funeral-livestreaming-nairobi-kenya" replace />} />
        <Route path="/services/photography" element={<Navigate to="/funeral-photography-nairobi-kenya" replace />} />
        <Route path="/services/video-production" element={<Navigate to="/memorial-video-production-kenya" replace />} />
        <Route path="/services/drone-coverage" element={<Navigate to="/funeral-drone-coverage-kenya" replace />} />

        <Route path="/funeral-livestreaming-nairobi-kenya" element={<Layout><Suspense fallback={<LoadingSpinner />}><LivestreamingService /></Suspense></Layout>} />
        <Route path="/funeral-photography-nairobi-kenya" element={<Layout><Suspense fallback={<LoadingSpinner />}><PhotographyService /></Suspense></Layout>} />
        <Route path="/memorial-video-production-kenya" element={<Layout><Suspense fallback={<LoadingSpinner />}><VideoService /></Suspense></Layout>} />
        <Route path="/funeral-drone-coverage-kenya" element={<Layout><Suspense fallback={<LoadingSpinner />}><DroneService /></Suspense></Layout>} />
        <Route path="/funeral-live-streaming-kenya" element={<Layout><Suspense fallback={<LoadingSpinner />}><LivestreamingService /></Suspense></Layout>} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard
                bookings={adminData.bookings}
                leads={adminData.leads}
                reviews={adminData.reviews}
                posts={adminData.posts}
              />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Layout><Suspense fallback={<LoadingSpinner />}><NotFound /></Suspense></Layout>} />
      </Routes>
    </Router>
  );
}

export default App;
